import React, { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutGrid, FolderKanban, MailQuestion, LogOut, Plus, Trash2, Pencil,
  X, Upload, Star, ExternalLink, Loader2, ChevronUp, ChevronDown, MessageSquareQuote, ImageUp,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { resolveImage } from "@/lib/api";

const CATEGORIES = ["Residential", "Commercial", "Waterfront", "Land Development"];
const emptyProject = { title: "", category: "Residential", location: "", year: "", description: "", image_paths: [], captions: [], featured: false };
const emptyTestimonial = { name: "", role: "", quote: "", rating: 5, image_path: "", featured: true };
const STATUS_COLORS = { new: "bg-gold text-navy", "in-progress": "bg-blue-100 text-blue-800", closed: "bg-gray-200 text-gray-700" };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState({ total_projects: 0, total_quotes: 0, new_quotes: 0, total_testimonials: 0 });
  const [projects, setProjects] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editingT, setEditingT] = useState(null);

  const loadAll = async () => {
    const [s, p, q, t] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/projects"),
      api.get("/admin/quotes"),
      api.get("/testimonials"),
    ]);
    setStats(s.data); setProjects(p.data); setQuotes(q.data); setTestimonials(t.data);
  };

  useEffect(() => { loadAll().catch(() => toast.error("Failed to load data")); }, []);

  const handleLogout = () => { logout(); navigate("/admin/login"); };

  const deleteProject = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/admin/projects/${id}`);
    toast.success("Project deleted");
    loadAll();
  };

  const updateQuoteStatus = async (id, status) => {
    await api.put(`/admin/quotes/${id}`, { status });
    toast.success("Status updated");
    loadAll();
  };

  const deleteQuote = async (id) => {
    if (!window.confirm("Delete this quote request?")) return;
    await api.delete(`/admin/quotes/${id}`);
    loadAll();
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    toast.success("Testimonial deleted");
    loadAll();
  };

  const navItems = [
    { key: "overview", label: "Overview", icon: LayoutGrid },
    { key: "projects", label: "Projects", icon: FolderKanban },
    { key: "testimonials", label: "Testimonials", icon: MessageSquareQuote },
    { key: "quotes", label: "Quote Requests", icon: MailQuestion },
  ];

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-navy text-white flex flex-col fixed inset-y-0 z-20">
        <div className="p-6 border-b border-white/10 flex items-center gap-3">
          <img src="/logo.png" alt="logo" className="h-10 w-10 rounded-full bg-white ring-1 ring-gold/50" />
          <div><div className="font-display font-bold text-sm">Crawford</div><div className="text-[10px] tracking-widest text-gold">ADMIN</div></div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((n) => (
            <button key={n.key} data-testid={`tab-${n.key}`} onClick={() => setTab(n.key)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${tab === n.key ? "bg-gold text-navy" : "text-white/70 hover:bg-white/5"}`}>
              <n.icon size={18} /> {n.label}
              {n.key === "quotes" && stats.new_quotes > 0 && <span className="ml-auto bg-gold text-navy text-xs px-2 rounded-full">{stats.new_quotes}</span>}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-gold text-sm px-4 py-2"><ExternalLink size={15} /> View Site</Link>
          <button data-testid="logout-btn" onClick={handleLogout} className="w-full flex items-center gap-2 text-white/60 hover:text-gold text-sm px-4 py-2"><LogOut size={15} /> Logout</button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-64">
        <header className="bg-white border-b px-8 py-5 flex justify-between items-center">
          <h1 className="font-display text-2xl text-navy capitalize">{tab === "quotes" ? "Quote Requests" : tab}</h1>
          <div className="text-sm text-muted-foreground">{user?.email}</div>
        </header>

        <div className="p-8">
          {tab === "overview" && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard label="Total Projects" value={stats.total_projects} icon={FolderKanban} />
              <StatCard label="Testimonials" value={stats.total_testimonials} icon={MessageSquareQuote} />
              <StatCard label="Quote Requests" value={stats.total_quotes} icon={MailQuestion} />
              <StatCard label="New Requests" value={stats.new_quotes} icon={Star} accent />
            </div>
          )}

          {tab === "projects" && (
            <>
              <div className="flex justify-end mb-6">
                <button data-testid="add-project-btn" onClick={() => setEditing(emptyProject)} className="flex items-center gap-2 bg-navy text-white px-5 py-2.5 text-sm font-semibold hover:bg-navy-light transition-colors"><Plus size={16} /> Add Project</button>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {projects.map((p) => (
                  <div key={p.id} className="bg-white border overflow-hidden" data-testid={`admin-project-${p.id}`}>
                    <div className="h-40 bg-slate-100 relative">
                      {p.image_paths?.[0] && <img src={resolveImage(p.image_paths[0])} alt={p.title} className="w-full h-full object-cover" />}
                      {p.featured && <span className="absolute top-2 left-2 bg-gold text-navy text-[10px] font-bold px-2 py-0.5 flex items-center gap-1"><Star size={10} /> FEATURED</span>}
                    </div>
                    <div className="p-4">
                      <div className="text-xs text-gold font-semibold uppercase">{p.category}</div>
                      <h3 className="font-semibold text-navy mt-1">{p.title}</h3>
                      <p className="text-xs text-muted-foreground mt-1">{p.location}</p>
                      <div className="flex gap-2 mt-4">
                        <button onClick={() => setEditing(p)} data-testid={`edit-${p.id}`} className="flex-1 flex items-center justify-center gap-1 border border-navy text-navy py-2 text-xs font-semibold hover:bg-navy hover:text-white transition-colors"><Pencil size={13} /> Edit</button>
                        <button onClick={() => deleteProject(p.id)} data-testid={`delete-${p.id}`} className="flex items-center justify-center border border-red-200 text-red-600 px-3 hover:bg-red-600 hover:text-white transition-colors"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {tab === "testimonials" && (
            <>
              <div className="flex justify-end mb-6">
                <button data-testid="add-testimonial-btn" onClick={() => setEditingT(emptyTestimonial)} className="flex items-center gap-2 bg-navy text-white px-5 py-2.5 text-sm font-semibold hover:bg-navy-light transition-colors"><Plus size={16} /> Add Testimonial</button>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {testimonials.length === 0 && <p className="text-muted-foreground col-span-full">No testimonials yet. Add your first client review.</p>}
                {testimonials.map((t) => (
                  <div key={t.id} className="bg-white border p-6" data-testid={`admin-testimonial-${t.id}`}>
                    <div className="flex gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, s) => <Star key={s} size={14} className={s < (t.rating || 5) ? "fill-gold text-gold" : "text-gray-300"} />)}
                    </div>
                    <p className="text-sm text-navy line-clamp-4">“{t.quote}”</p>
                    <div className="mt-4 flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-navy text-sm">{t.name}</div>
                        <div className="text-xs text-muted-foreground">{t.role}</div>
                      </div>
                      {t.featured && <span className="bg-gold text-navy text-[10px] font-bold px-2 py-0.5">FEATURED</span>}
                    </div>
                    <div className="flex gap-2 mt-4">
                      <button onClick={() => setEditingT(t)} data-testid={`edit-testimonial-${t.id}`} className="flex-1 flex items-center justify-center gap-1 border border-navy text-navy py-2 text-xs font-semibold hover:bg-navy hover:text-white transition-colors"><Pencil size={13} /> Edit</button>
                      <button onClick={() => deleteTestimonial(t.id)} data-testid={`delete-testimonial-${t.id}`} className="flex items-center justify-center border border-red-200 text-red-600 px-3 hover:bg-red-600 hover:text-white transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {tab === "quotes" && (
            <div className="bg-white border overflow-hidden">
              <table className="w-full text-sm">                <thead className="bg-slate-50 border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-5 py-3">Name</th><th className="px-5 py-3">Contact</th>
                    <th className="px-5 py-3">Service</th><th className="px-5 py-3">Message</th>
                    <th className="px-5 py-3">Status</th><th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {quotes.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-muted-foreground">No quote requests yet.</td></tr>}
                  {quotes.map((q) => (
                    <tr key={q.id} data-testid={`quote-row-${q.id}`} className="hover:bg-slate-50">
                      <td className="px-5 py-4 font-semibold text-navy">{q.name}</td>
                      <td className="px-5 py-4 text-muted-foreground"><div>{q.email}</div><div className="text-xs">{q.phone}</div></td>
                      <td className="px-5 py-4 text-muted-foreground">{q.service || "—"}</td>
                      <td className="px-5 py-4 text-muted-foreground max-w-xs"><div className="line-clamp-2">{q.message}</div></td>
                      <td className="px-5 py-4">
                        <select value={q.status} onChange={(e) => updateQuoteStatus(q.id, e.target.value)} data-testid={`quote-status-${q.id}`} className={`text-xs font-bold px-2 py-1 border-0 ${STATUS_COLORS[q.status] || "bg-gray-100"}`}>
                          <option value="new">New</option><option value="in-progress">In Progress</option><option value="closed">Closed</option>
                        </select>
                      </td>
                      <td className="px-5 py-4"><button onClick={() => deleteQuote(q.id)} className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editing && <ProjectModal project={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); loadAll(); }} />}
      {editingT && <TestimonialModal item={editingT} onClose={() => setEditingT(null)} onSaved={() => { setEditingT(null); loadAll(); }} />}
    </div>
  );
}

const StatCard = ({ label, value, icon: Icon, accent }) => (
  <div className={`p-7 border ${accent ? "bg-navy text-white border-navy" : "bg-white"}`}>
    <div className="flex justify-between items-start">
      <div>
        <div className={`text-4xl font-display ${accent ? "text-gold" : "text-navy"}`}>{value}</div>
        <div className={`text-sm mt-1 ${accent ? "text-white/60" : "text-muted-foreground"}`}>{label}</div>
      </div>
      <Icon size={28} className={accent ? "text-gold" : "text-gold"} />
    </div>
  </div>
);

function ProjectModal({ project, onClose, onSaved }) {
  const [form, setForm] = useState({ ...project, image_paths: project.image_paths || [], captions: project.captions || [] });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const isEdit = !!project.id;

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const paths = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(res.data.path);
      }
      setForm((prev) => ({ ...prev, image_paths: [...prev.image_paths, ...paths], captions: [...prev.captions, ...paths.map(() => "")] }));
      toast.success("Image(s) uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeImg = (i) => setForm({
    ...form,
    image_paths: form.image_paths.filter((_, idx) => idx !== i),
    captions: form.captions.filter((_, idx) => idx !== i),
  });

  const setCaption = (i, val) => {
    const next = [...form.captions];
    while (next.length < form.image_paths.length) next.push("");
    next[i] = val;
    setForm({ ...form, captions: next });
  };

  const moveImg = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= form.image_paths.length) return;
    const imgs = [...form.image_paths];
    const caps = [...form.captions];
    while (caps.length < imgs.length) caps.push("");
    [imgs[i], imgs[j]] = [imgs[j], imgs[i]];
    [caps[i], caps[j]] = [caps[j], caps[i]];
    setForm({ ...form, image_paths: imgs, captions: caps });
  };

  const setCover = (i) => {
    if (i === 0) return;
    const imgs = [...form.image_paths];
    const caps = [...form.captions];
    while (caps.length < imgs.length) caps.push("");
    const [img] = imgs.splice(i, 1);
    const [cap] = caps.splice(i, 1);
    imgs.unshift(img);
    caps.unshift(cap);
    setForm({ ...form, image_paths: imgs, captions: caps });
    toast.success("Cover photo updated");
  };

  const save = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      if (isEdit) await api.put(`/admin/projects/${project.id}`, form);
      else await api.post("/admin/projects", form);
      toast.success(isEdit ? "Project updated" : "Project created");
      onSaved();
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-navy/70 flex items-center justify-center p-4 overflow-y-auto" data-testid="project-modal-admin">
      <div className="bg-white w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center px-6 py-4 border-b sticky top-0 bg-white">
          <h2 className="font-display text-xl text-navy">{isEdit ? "Edit Project" : "Add Project"}</h2>
          <button onClick={onClose}><X size={22} className="text-navy" /></button>
        </div>
        <div className="p-6 space-y-5">
          <Input label="Title *" value={form.title} onChange={upd("title")} testid="pm-title" />
          <div className="grid sm:grid-cols-2 gap-5">
            <label className="block">
              <span className="block text-xs uppercase tracking-widest font-semibold text-navy mb-2">Category</span>
              <select value={form.category} onChange={upd("category")} data-testid="pm-category" className="w-full border border-gray-200 px-4 py-2.5 outline-none focus:border-gold">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </label>
            <Input label="Year" value={form.year} onChange={upd("year")} testid="pm-year" />
          </div>
          <Input label="Location" value={form.location} onChange={upd("location")} testid="pm-location" />
          <label className="block">
            <span className="block text-xs uppercase tracking-widest font-semibold text-navy mb-2">Description</span>
            <textarea value={form.description} onChange={upd("description")} rows={4} data-testid="pm-description" className="w-full border border-gray-200 px-4 py-2.5 outline-none focus:border-gold resize-none" />
          </label>

          <div>
            <span className="block text-xs uppercase tracking-widest font-semibold text-navy mb-2">Photos & Captions</span>
            <p className="text-xs text-muted-foreground mb-3">Add a caption to each photo to tell the build story (e.g. "Stage 1 — Foundation"). The <b>first photo is the cover</b> shown on cards — use "Set cover" to change it.</p>
            <div className="space-y-3 mb-4">
              {form.image_paths.map((p, i) => (
                <div key={i} className="flex items-center gap-3 border border-gray-100 p-2" data-testid={`pm-photo-row-${i}`}>
                  <div className="relative shrink-0">
                    <img src={resolveImage(p)} alt="" className="h-16 w-16 object-cover border" />
                    {i === 0 && <span className="absolute -top-2 -left-1 bg-gold text-navy text-[9px] font-bold px-1.5 py-0.5">COVER</span>}
                  </div>
                  <input
                    value={form.captions[i] || ""}
                    onChange={(e) => setCaption(i, e.target.value)}
                    data-testid={`pm-caption-${i}`}
                    placeholder={`Caption for photo ${i + 1} (optional)`}
                    className="flex-1 border border-gray-200 px-3 py-2 text-sm outline-none focus:border-gold"
                  />
                  {i !== 0 && (
                    <button type="button" onClick={() => setCover(i)} data-testid={`pm-setcover-${i}`} title="Set as cover photo" className="flex items-center gap-1 text-xs font-semibold text-navy border border-gray-200 px-2 py-1.5 hover:border-gold hover:text-gold transition-colors">
                      <ImageUp size={13} /> Cover
                    </button>
                  )}
                  <div className="flex flex-col">
                    <button type="button" onClick={() => moveImg(i, -1)} disabled={i === 0} data-testid={`pm-moveup-${i}`} className="text-navy disabled:opacity-30 hover:text-gold"><ChevronUp size={16} /></button>
                    <button type="button" onClick={() => moveImg(i, 1)} disabled={i === form.image_paths.length - 1} data-testid={`pm-movedown-${i}`} className="text-navy disabled:opacity-30 hover:text-gold"><ChevronDown size={16} /></button>
                  </div>
                  <button type="button" onClick={() => removeImg(i)} data-testid={`pm-removeimg-${i}`} className="text-red-500 hover:text-red-700 shrink-0"><X size={18} /></button>
                </div>
              ))}
              {form.image_paths.length === 0 && <p className="text-sm text-muted-foreground italic">No photos yet.</p>}
            </div>
            <input type="file" ref={fileRef} accept="image/*" multiple hidden onChange={onUpload} data-testid="pm-file-input" />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-2 border border-navy text-navy px-4 py-2 text-sm font-semibold hover:bg-navy hover:text-white transition-colors disabled:opacity-60">
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {uploading ? "Uploading..." : "Upload Photos"}
            </button>
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="pm-featured" className="h-4 w-4 accent-gold" />
            <span className="text-sm font-medium text-navy">Show on homepage (Featured)</span>
          </label>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-5 py-2.5 text-sm font-semibold text-navy border border-gray-200">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="pm-save" className="px-6 py-2.5 text-sm font-bold bg-navy text-white hover:bg-navy-light transition-colors disabled:opacity-60">{saving ? "Saving..." : "Save Project"}</button>
        </div>
      </div>
    </div>
  );
}

const Input = ({ label, value, onChange, testid }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-widest font-semibold text-navy mb-2">{label}</span>
    <input value={value} onChange={onChange} data-testid={testid} className="w-full border border-gray-200 px-4 py-2.5 outline-none focus:border-gold" />
  </label>
);


function TestimonialModal({ item, onClose, onSaved }) {
  const [form, setForm] = useState(item);
  const [saving, setSaving] = useState(false);
  const isEdit = !!item.id;
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.name || !form.quote) { toast.error("Name and quote are required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, rating: Number(form.rating) || 5 };
      if (isEdit) await api.put(`/admin/testimonials/${item.id}`, payload);
      else await api.post("/admin/testimonials", payload);
      toast.success(isEdit ? "Testimonial updated" : "Testimonial added");
      onSaved();
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-navy/70 flex items-center justify-center p-4 overflow-y-auto" data-testid="testimonial-modal">
      <div className="bg-white w-full max-w-lg my-8">
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <h2 className="font-display text-xl text-navy">{isEdit ? "Edit Testimonial" : "Add Testimonial"}</h2>
          <button onClick={onClose}><X size={22} className="text-navy" /></button>
        </div>
        <div className="p-6 space-y-5">
          <Input label="Client Name *" value={form.name} onChange={upd("name")} testid="tm-name" />
          <Input label="Role / Location (e.g. Homeowner, Placencia)" value={form.role} onChange={upd("role")} testid="tm-role" />
          <label className="block">
            <span className="block text-xs uppercase tracking-widest font-semibold text-navy mb-2">Testimonial *</span>
            <textarea value={form.quote} onChange={upd("quote")} rows={4} data-testid="tm-quote" className="w-full border border-gray-200 px-4 py-2.5 outline-none focus:border-gold resize-none" placeholder="What the client said about your work..." />
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-widest font-semibold text-navy mb-2">Rating</span>
            <select value={form.rating} onChange={upd("rating")} data-testid="tm-rating" className="w-full border border-gray-200 px-4 py-2.5 outline-none focus:border-gold">
              {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r} Star{r > 1 ? "s" : ""}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="tm-featured" className="h-4 w-4 accent-gold" />
            <span className="text-sm font-medium text-navy">Show on homepage (Featured)</span>
          </label>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 text-sm font-semibold text-navy border border-gray-200">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="tm-save" className="px-6 py-2.5 text-sm font-bold bg-navy text-white hover:bg-navy-light transition-colors disabled:opacity-60">{saving ? "Saving..." : "Save"}</button>
        </div>
      </div>
    </div>
  );
}